import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faDownload, faFileWord, faFilePdf, faFileAlt } from '@fortawesome/free-solid-svg-icons';

import api from '../../api';
import axiosInstance from '../../api/axios';
import YouTubePlayer, { getYouTubeVideoId } from '../../components/YouTubePlayer';
import { isAudioType, isVideoType } from '../../catalog/catalogGroups';
import { useTheme } from '../../context/ThemeProvider';
import { PAGE_CONFIG } from './Config';
import { toastStyles } from '../../styles/styles';

const materialTypeName = (item) =>
    item?.material_type?.name || item?.MaterialType?.name || '';

const isPlayableMedia = (item, documentType) => {
    const typeName = materialTypeName(item) || documentType;
    return isVideoType(typeName) || isAudioType(typeName) || Boolean(getYouTubeVideoId(item?.url));
};

const Cases = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const { state } = useLocation();
    const [searchParams] = useSearchParams();

    const documentId = routeId || state?.id || searchParams.get('id');
    const documentType = searchParams.get('type') || state?.documentType;
    

    const tr = useCallback((key) => t(`cases:${key}`), [t]);
    const pageConfig = useMemo(() => PAGE_CONFIG(tr), [tr]);

    const [manual, setManual] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [isManualLoading, setIsManualLoading] = useState(false);
    const [fileType, setFileType] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [showOfficeOnline, setShowOfficeOnline] = useState(false);
    const [officeOnlineUrl, setOfficeOnlineUrl] = useState(null);

    const { data, error, isLoading, isError } = useQuery({
        queryKey: ['get-document', documentId, documentType],
        queryFn: () => {
            if (documentType === 'constitution') {
                return api.constitutions.getConstitution(documentId);
            } else {
                return api.materials.getMaterial(documentId);
            }
        },
        enabled: !!documentId,
        cacheTime: 0,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        select: (response) => response.data.data,
        onError: (error) => {
            console.error('Error fetching document:', error);
            toast.error(error.message, { ...toastStyles, theme });
        }
    });

    // Helper function to get file type from filename or content type
    const getFileType = (filename, contentType) => {
        if (!filename && !contentType) return 'unknown';
        
        const extension = filename ? filename.split('.').pop()?.toLowerCase() : '';
        const mimeType = contentType ? contentType.toLowerCase() : '';
        
        // PDF files
        if (extension === 'pdf' || mimeType.includes('pdf')) return 'pdf';
        
        // Word documents
        if (extension === 'docx' || mimeType.includes('docx')) return 'docx';
        if (extension === 'doc' || mimeType.includes('doc')) return 'doc';
        if (mimeType.includes('word') || mimeType.includes('msword')) return 'docx';
        
        // Excel files
        if (extension === 'xlsx' || extension === 'xls' || mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'excel';
        
        // PowerPoint files
        if (extension === 'pptx' || extension === 'ppt' || mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'powerpoint';
        
        // Text files
        if (extension === 'txt' || mimeType.includes('text/plain')) return 'text';
        
        // RTF files
        if (extension === 'rtf' || mimeType.includes('rtf')) return 'rtf';
        
        return 'unknown';
    };

    // Helper function to handle file download
    const handleDownload = async () => {
        if (!data?.manual?.uri) {
            toast.error('No file available for download', { ...toastStyles, theme });
            return;
        }

        try {
            const res = await axiosInstance.get(`/fsmanuals/${data.manual.uri}`, {
                responseType: 'blob',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                    Expires: '0'
                }
            });
        

            const fileBlob = new Blob([res.data], { type: res.data.type });
            const url = URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || data.title || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            toast.success('File downloaded successfully', { ...toastStyles, theme });
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error('Failed to download file', { ...toastStyles, theme });
        }
    };

    // Helper function to open file in Microsoft Office Online Viewer
    const handleOpenInOfficeOnline = async () => {
        if (!data?.manual?.uri) {
            toast.error('No file available to open', { ...toastStyles, theme });
            return;
        }

        try {
            // Get the file URL from your server
            const fileUrl = `${axiosInstance.defaults.baseURL}/fsmanuals/${data.manual.uri}`;
            
            // Microsoft Office Online Viewer URL
            const officeOnlineUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
            
            // Open in new tab
            window.open(officeOnlineUrl, '_blank');
        } catch (error) {
            console.error('Error opening file in Office Online:', error);
            toast.error('Failed to open file in Office Online', { ...toastStyles, theme });
        }
    };

    // Helper function to view file inline using Office Online
    const handleViewInline = async () => {
        if (!data?.manual?.uri) {
            toast.error('No file available to view', { ...toastStyles, theme });
            return;
        }

        try {
            // Get the file URL from your server
            const fileUrl = `${axiosInstance.defaults.baseURL}/fsmanuals/${data.manual.uri}`;
            
            // Microsoft Office Online Viewer URL
            const officeOnlineUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
            
            setOfficeOnlineUrl(officeOnlineUrl);
            setShowOfficeOnline(true);
        } catch (error) {
            console.error('Error opening file in Office Online:', error);
            toast.error('Failed to open file in Office Online', { ...toastStyles, theme });
        }
    };

    useEffect(() => {
        if (!data) return;
        const fetchManual = async () => {
            if (isPlayableMedia(data, documentType)) {
                setVideoUrl(data.url || state?.url || null);
                setIsVideoLoading(true);
                return;
            }

            if (!data.manual?.uri) {
                setManual(null);
                return;
            }

            setIsManualLoading(true);
            try {
                const res = await axiosInstance.get(`/fsmanuals/${data.manual.uri}`, {
                    responseType: 'blob',
                    headers: {
                        'Cache-Control': 'no-cache',
                        Pragma: 'no-cache',
                        Expires: '0'
                    }
                });
                
                const fileBlob = new Blob([res.data], { type: res.data.type });
                const detectedFileType = getFileType(data.manual.name || data.title, res.data.type);
                setFileType(detectedFileType);
                setFileName(data.manual.name || data.title);
                
                // Only create blob URL for PDF files that can be displayed in iframe
                if (detectedFileType === 'pdf') {
                    setManual(URL.createObjectURL(fileBlob));
                } else {
                    setManual(null);
                }
            } catch (error) {
                console.error('Error fetching manual:', error);
                setManual(null);
            } finally {
                setIsManualLoading(false);
            }
        };

        fetchManual();

        return () => {
            if (manual) {
                URL.revokeObjectURL(manual);
            }
        };
    }, [data, documentType, state?.url]);

   

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[400px] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8">
            <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin">
                    <FontAwesomeIcon icon={faSpinner} className="w-12 h-12 text-gray-400 dark:text-[#3590BA]" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                        Preparing your document...
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Please wait while we load the content for you
                    </p>
                </div>
            </div>
        </div>
    );
    if (isError) return <div className="text-center mt-8">{pageConfig.DEFAULT_ERROR_MESSAGE}</div>;

    const VideoContent = () => {
        const videoId = getYouTubeVideoId(videoUrl);
        const isAudio = isAudioType(materialTypeName(data) || documentType);

        if (!videoUrl) {
            return <p>{isAudio ? 'No audio found' : 'No video found'}</p>;
        }

        return (
            <div className="relative w-full flex justify-center">
                {isVideoLoading && videoId ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-200 dark:bg-gray-800 rounded-lg">
                        <p>{isAudio ? 'Loading audio...' : 'Loading video...'}</p>
                    </div>
                ) : null}
                {videoId ? (
                    <YouTubePlayer
                        videoId={videoId}
                        title={`${isAudio ? 'Audio' : 'Video'} - ${data.title}`}
                        fit="page"
                        onLoad={() => setIsVideoLoading(false)}
                    />
                ) : (
                    <audio
                        controls
                        src={videoUrl}
                        className="w-full"
                        onCanPlay={() => setIsVideoLoading(false)}
                    >
                        Your browser does not support the audio element.
                    </audio>
                )}
            </div>
        );
    };

    const getFileIcon = (type) => {
        switch (type) {
            case 'pdf':
                return faFilePdf;
            case 'docx':
            case 'doc':
                return faFileWord;
            case 'excel':
            case 'xlsx':
            case 'xls':
                return faFileAlt;
            case 'powerpoint':
            case 'pptx':
            case 'ppt':
                return faFileAlt;
            case 'text':
            case 'txt':
                return faFileAlt;
            case 'rtf':
                return faFileAlt;
            default:
                return faFileAlt;
        }
    };

    const getFileTypeLabel = (type) => {
        switch (type) {
            case 'pdf':
                return 'PDF Document';
            case 'docx':
                return 'Word Document (DOCX)';
            case 'doc':
                return 'Word Document (DOC)';
            case 'excel':
            case 'xlsx':
            case 'xls':
                return 'Excel Spreadsheet';
            case 'powerpoint':
            case 'pptx':
            case 'ppt':
                return 'PowerPoint Presentation';
            case 'text':
            case 'txt':
                return 'Text Document';
            case 'rtf':
                return 'Rich Text Document';
            default:
                return 'Document';
        }
    };

    const Content = () => {
        return (
            <div className="relative">
                {isManualLoading ? (
                    <div className="flex items-center justify-center bg-gray-200 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 min-h-[400px]">
                        <div className="flex items-center space-x-4">
                            <div className="animate-spin">
                                <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-gray-400 dark:text-[#3590BA]" />
                            </div>
                            <span className="text-gray-800 dark:text-gray-200 text-lg">Please wait while we are loading the document...</span>
                        </div>
                    </div>
                ) : manual && fileType === 'pdf' ? (
                    <iframe 
                        src={manual} 
                        width="100%" 
                        height="600px" 
                        className="border rounded-lg" 
                        title="Document Viewer"
                        onLoad={() => setIsManualLoading(false)} 
                        onError={() => setIsManualLoading(false)} 
                    />
                ) : showOfficeOnline && officeOnlineUrl ? (
                    <div className="relative">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                                Viewing: {fileName || 'Document'}
                            </h3>
                            <button
                                onClick={() => setShowOfficeOnline(false)}
                                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                            >
                                Close Viewer
                            </button>
                        </div>
                        <iframe 
                            src={officeOnlineUrl} 
                            width="100%" 
                            height="600px" 
                            className="border rounded-lg" 
                            title="Office Online Viewer"
                            frameBorder="0"
                        />
                    </div>
                ) : fileType && fileType !== 'unknown' ? (
                    <div className="flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-12 min-h-[400px]">
                        <div className="text-center space-y-6">
                            <div className="flex justify-center">
                                <FontAwesomeIcon 
                                    icon={getFileIcon(fileType)} 
                                    className="w-24 h-24 text-gray-400 dark:text-gray-500" 
                                />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                                    {fileName || 'Document'}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {getFileTypeLabel(fileType)}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-500 max-w-md">
                                    {fileType === 'pdf' 
                                        ? 'This PDF document can be viewed directly in the browser.'
                                        : 'This file type cannot be previewed directly in the browser. You can download it or open it in Office Online.'
                                    }
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={handleDownload}
                                    className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                                >
                                    <FontAwesomeIcon icon={faDownload} className="w-4 h-4 mr-2" />
                                    Download Document
                                </button>
                              
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center bg-gray-200 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 min-h-[400px]">
                        <div className="text-center">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                Content Unavailable
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                The document content is not available at the moment.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="mx-auto">
            <button onClick={() => navigate(-1)} className="mb-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                ← Back
            </button>

            <div className="w-full bg-white dark:bg-gray-800 shadow-lg p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-200">{data?.title}</p>
                <p className="text-lg font-light text-gray-800 dark:text-gray-200">{data?.description}</p>
            </div>

            <div className="w-full h-full text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 shadow-lg p-6 rounded-lg border border-gray-200 dark:border-gray-700 mt-8">
                {isPlayableMedia(data, documentType) ? <VideoContent /> : <Content />}
            </div>
        </div>
    );
};

export default Cases;
